import type { TDealTasksData } from '@crm/entities/deal';
import type {
    IAttachmentResponse,
    TaskData,
    UpdatedTaskForEditModal,
} from '@entities/task';

type TDealTask = TDealTasksData['tasks'][number];
type TDealAttachment = NonNullable<TDealTask['attachments']>[number];

const isClientAttachment = (
    attachment: TDealAttachment
): attachment is Extract<TDealAttachment, { file: File }> => 'file' in attachment;

const mapAttachment = (attachment: TDealAttachment): IAttachmentResponse => {
    if (isClientAttachment(attachment)) {
        return {
            id: attachment.id,
            'mime_type': attachment.file.type,
            'original_url': '',
            'preview_svg_url': '',
            'preview_url': '',
            name: attachment.file.name,
        };
    }

    return {
        id: attachment.id,
        'mime_type': attachment.mimeType,
        'original_url': attachment.originalUrl,
        'preview_svg_url': attachment.previewSvgUrl,
        'preview_url': attachment.previewUrl,
        name: attachment.name,
    };
};

export const mapDealTaskToLegacyTask = (task: TDealTask): TaskData => ({
    uid: task.uid,
    type: task.type,
    completed: task.completed,
    category: task.category,
    title: task.title,
    description: task.description,
    'date_start': task.dateStart,
    'date_end': task.dateEnd,
    'project_id': task.projectId,
    'estimate_id': task.estimateId,
    involved: task.involved
        ? {
            'author_id': task.involved.authorId,
            'executor_id': task.involved.executorId,
            'observer_ids': task.involved.observerIds,
        }
        : null,
    context: {
        'redirect_url': task.context.redirectUrl,
        tags: task.context.tags,
        color: task.context.color,
        description: task.context.description,
        'highest_role': task.context.highestRole,
        'created_at': task.context.createdAt,
        'entity_uid': task.context.entityUid,
        status: task.context.status,
    },
    attachments: task.attachments?.map(mapAttachment) ?? [],
});

export const mapDealTaskToEditModalTask = ({
    tagTitles,
    task,
}: {
    tagTitles: string[];
    task: TDealTask;
}): UpdatedTaskForEditModal => {
    const legacyTask = mapDealTaskToLegacyTask(task);

    return {
        ...legacyTask,
        context: {
            ...legacyTask.context,
            tags: tagTitles,
        },
    };
};
